import React from "react";
import PropTypes from "prop-types";
import {
  resolveEquipDiscount,
  isPercentageDiscount,
} from "../../../utils/equipDiscount";

const money = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// One cell of the price strip across the top. `was` is struck through only when
// it is genuinely higher than what the customer now pays.
const PriceCell = ({ label, was, now, text, footnote }) => (
  <div className="flex-1 min-w-[135px] bg-white px-4 py-3">
    <div className="text-[13px] font-semibold text-gray-600 mb-1">{label}</div>
    {text ? (
      <div className="text-2xl font-bold text-gray-900">{text}</div>
    ) : (
      <div className="flex items-baseline gap-2 flex-wrap">
        {was > now && (
          <span className="text-sm font-semibold text-gray-500 line-through">
            {money(was)}
          </span>
        )}
        <span className="text-2xl leading-tight font-bold text-gray-900">
          {money(now)}
        </span>
      </div>
    )}
    {footnote && (
      <div className="text-[12.5px] font-semibold text-amber-700 mt-1">
        {footnote}
      </div>
    )}
  </div>
);

PriceCell.propTypes = {
  label: PropTypes.string,
  was: PropTypes.number,
  now: PropTypes.number,
  text: PropTypes.string,
  footnote: PropTypes.string,
};

const OrderSummaryCard = ({
  setUpFee,
  monthlyFee,
  planType,
  equipDiscount,
  equipDiscountType,
  qualifyingEquipmentPrice,
  discountOnSetupFee,
  planPermDiscount,
  planTempDiscount,
  tempDiscountExpiryDate,
  qualifyingPlanPrice,
  discountOnMonthlyFee,
  installments,
  dueToday,
  minimumForInstallments,
  showInstallmentInfo,
  monthlySMSQuota,
  priceAdd1KSMS,
}) => {
  // A missing expiry date counts as expired rather than as "never expires".
  const isTempExpired =
    !tempDiscountExpiryDate || new Date() > new Date(tempDiscountExpiryDate);
  const tempDiscount = isTempExpired ? 0 : Number(planTempDiscount) || 0;
  const permDiscount = Number(planPermDiscount) || 0;

  // A discount can exist on the order without qualifying yet. Those two states
  // have to stay distinct everywhere: a negotiated discount the customer has
  // not unlocked must never be presented as money already taken off.
  // Proma may send this as a percentage of the equipment total rather than a
  // flat amount, so it has to be resolved to dollars before anything below
  // treats it as money.
  const equipDiscountAmount = resolveEquipDiscount(
    equipDiscount,
    equipDiscountType,
    setUpFee
  );
  const hasEquipmentDiscount = equipDiscountAmount > 0;
  // A qualifying price of 0 means no threshold was negotiated, so the discount
  // is unconditional. Requiring one here used to drop the discount silently:
  // the hook sets discountOnSetupFee (setUpFee >= 0 is always true) while this
  // card showed neither the row nor the reduction.
  const hasEquipmentThreshold = Number(qualifyingEquipmentPrice) > 0;
  const setupApplied = hasEquipmentDiscount && !!discountOnSetupFee;
  const setupPending = hasEquipmentDiscount && !discountOnSetupFee;

  const hasPlanDiscount = permDiscount > 0 || tempDiscount > 0;
  const monthlyApplied = hasPlanDiscount && !!discountOnMonthlyFee;
  const monthlyPending = hasPlanDiscount && !discountOnMonthlyFee;
  const planOff = permDiscount + tempDiscount;

  // Only subtract a discount from the displayed price when it actually applies.
  const setupOff = setupApplied ? equipDiscountAmount : 0;
  const monthlyOff = monthlyApplied ? planOff : 0;

  const setupNow = Number(setUpFee || 0) - setupOff;
  const monthlyNow = Number(monthlyFee || 0) - monthlyOff;
  // Once the temporary discount lapses only the permanent one remains.
  const monthlyLater =
    Number(monthlyFee || 0) - (monthlyApplied ? permDiscount : 0);
  const showsTempStepDown =
    monthlyApplied && tempDiscount > 0 && monthlyLater > monthlyNow;

  const planUnlock =
    Number(qualifyingPlanPrice) > 0
      ? `Needs a plan of ${money(qualifyingPlanPrice)} or more`
      : "Does not apply to your current plan";

  // Footnotes sit directly under the price they qualify, so an unearned
  // discount is visible at the price itself and not only in the list below.
  const equipUnlock = hasEquipmentThreshold
    ? `Needs ${money(qualifyingEquipmentPrice)} or more in equipment`
    : "Does not apply to your current equipment";

  let setupFootnote = null;
  if (setupPending && hasEquipmentThreshold) {
    setupFootnote = `−${money(
      equipDiscountAmount
    )} once equipment reaches ${money(qualifyingEquipmentPrice)}`;
  }

  let monthlyFootnote = null;
  if (monthlyPending && Number(qualifyingPlanPrice) > 0) {
    monthlyFootnote = `−${money(planOff)}/mo on plans from ${money(
      qualifyingPlanPrice
    )}`;
  } else if (showsTempStepDown) {
    monthlyFootnote = `${money(monthlyLater)} from ${formatDate(
      tempDiscountExpiryDate
    )}`;
  }

  const rows = [];
  if (hasEquipmentDiscount) {
    rows.push({
      key: "equip",
      // Show the RATE alongside the money when the discount was negotiated as a
      // percentage. Without it the row reads "−$119.40" and a customer cannot
      // tell whether the 20% they agreed was applied to the right base.
      label: isPercentageDiscount(equipDiscountType)
        ? `Setup cost (${Number(equipDiscount) || 0}% off)`
        : "Setup cost",
      amount: `−${money(equipDiscountAmount)}`,
      applied: setupApplied,
      unlock: equipUnlock,
    });
  }
  if (permDiscount > 0) {
    rows.push({
      key: "perm",
      label: `Monthly, expires on ${formatDate(tempDiscountExpiryDate)}`,
      amount: `−${money(permDiscount)}/mo`,
      applied: monthlyApplied,
      unlock: planUnlock,
    });
  }
  if (tempDiscount > 0) {
    rows.push({
      key: "temp",
      label: "Monthly, till agreement term",
      amount: `−${money(planTempDiscount)}/mo`,
      applied: monthlyApplied,
      unlock: planUnlock,
    });
  }
  const appliedCount = rows.filter((row) => row.applied).length;

  const showSchedule = showInstallmentInfo
    ? Number(installments) > 0
    : Number(minimumForInstallments) > 0 && Number(installments) > 0;
  const remaining = Number(installments) - 1;
  const showSms = Number(monthlySMSQuota) > 0;

  // The description is built as separate paragraphs so each one can be omitted
  // when it does not apply, rather than producing a sentence with holes in it.
  const paragraphs = [];

  // 1. What the setup cost actually is, and how it gets paid.
  if (Number(setUpFee) > 0) {
    let setupText =
      "Your setup cost is a one-time charge covering your equipment and account setup. It is separate from the monthly fee and is not billed again.";
    if (showInstallmentInfo && Number(installments) > 1) {
      setupText += ` You do not have to pay it in one go: it is split into ${installments} payments of ${money(
        dueToday
      )}, the first of which is due today.`;
    } else if (
      !showInstallmentInfo &&
      Number(minimumForInstallments) > 0 &&
      Number(setUpFee) > 0
    ) {
      setupText += ` It is charged up front — splitting it into installments becomes available once the setup cost passes ${money(
        minimumForInstallments
      )}.`;
    }
    paragraphs.push(setupText);
  }

  // 2. What was negotiated, and how much of it is actually in the prices above.
  if (rows.length > 0) {
    const effects = [];
    if (setupOff > 0 && Number(setUpFee) > 0) {
      effects.push(
        `your setup cost drops from ${money(setUpFee)} to ${money(setupNow)}`
      );
    }
    if (monthlyOff > 0 && Number(monthlyFee) > 0) {
      effects.push(
        `your monthly fee from ${money(monthlyFee)} to ${money(monthlyNow)}`
      );
    }
    if (appliedCount === 0) {
      paragraphs.push(
        `Your Growthzilla sales partner arranged ${
          rows.length > 1 ? "these reductions" : "this reduction"
        }, but ${
          rows.length > 1 ? "none of them apply" : "it does not apply"
        } to your current selection, so the prices above are the full amounts.`
      );
    } else {
      paragraphs.push(
        `Your Growthzilla sales partner arranged ${
          rows.length > 1 ? "these reductions" : "this reduction"
        }, and the prices above already include ${
          appliedCount < rows.length ? "the active ones" : "them"
        }${effects.length ? ` — ${effects.join(", and ")}` : ""}.`
      );
    }
  }

  // 3. What the active discounts depend on, and the risk of losing them.
  const conditions = [];
  if (setupOff > 0 && hasEquipmentThreshold) {
    conditions.push(
      `your equipment total staying above ${money(qualifyingEquipmentPrice)}`
    );
  }
  if (monthlyOff > 0 && Number(qualifyingPlanPrice) > 0) {
    conditions.push(`your plan staying above ${money(qualifyingPlanPrice)}`);
  }
  if (conditions.length) {
    paragraphs.push(
      `${
        appliedCount < rows.length ? "The active ones depend" : "They depend"
      } on ${conditions.join(
        " and "
      )}. If you change your selection and fall below ${
        conditions.length > 1 ? "either threshold" : "that threshold"
      }, the discount no longer applies.`
    );
  }

  // 4. Discounts that exist on the order but have not been earned yet, and the
  //    exact thing the customer has to change to earn them.
  const pending = [];
  if (setupPending) {
    pending.push(
      hasEquipmentThreshold
        ? `the ${money(
            equipDiscountAmount
          )} setup discount needs your equipment total to reach ${money(
            qualifyingEquipmentPrice
          )}`
        : `the ${money(
            equipDiscountAmount
          )} setup discount does not apply to your current equipment`
    );
  }
  if (monthlyPending) {
    pending.push(
      Number(qualifyingPlanPrice) > 0
        ? `the ${money(planOff)}/mo plan discount needs a plan of ${money(
            qualifyingPlanPrice
          )} or more`
        : `the ${money(
            planOff
          )}/mo plan discount does not apply to your current plan`
    );
  }
  if (pending.length) {
    paragraphs.push(
      `Not yet applied: ${pending.join(", and ")}. ${
        pending.length > 1 ? "They are" : "It is"
      } struck through in the list and ${
        pending.length > 1 ? "are" : "is"
      } not taken off the prices above. Adjust your selection below to meet ${
        pending.length > 1 ? "those requirements" : "that requirement"
      } and the ${
        pending.length > 1 ? "discounts come" : "discount comes"
      } off automatically.`
    );
  }

  // 5. How long each monthly reduction lasts. Only meaningful once the plan
  //    discount is actually active — otherwise "your fee becomes X" would
  //    quote the price the customer is already paying.
  if (monthlyApplied) {
    if (permDiscount > 0 && tempDiscount > 0) {
      paragraphs.push(
        `The ${money(
          permDiscount
        )} ongoing reduction is held for the whole agreement term. The ${money(
          tempDiscount
        )} limited-time reduction ends ${formatDate(
          tempDiscountExpiryDate
        )}, after which your monthly fee becomes ${money(monthlyLater)}.`
      );
    } else if (tempDiscount > 0) {
      paragraphs.push(
        `This reduction is limited-time: it ends ${formatDate(
          tempDiscountExpiryDate
        )}, after which your monthly fee becomes ${money(monthlyLater)}.`
      );
    } else if (permDiscount > 0) {
      paragraphs.push(
        `This reduction is held for the whole agreement term, with no expiry date.`
      );
    }
  }

  const hasExplanation = paragraphs.length > 0;

  return (
    <div className="text-left bg-white border border-gray-300 rounded-xl overflow-hidden my-4 shadow-sm">
      <div className="flex flex-wrap gap-px bg-gray-300">
        <PriceCell
          label="Setup cost"
          was={Number(setUpFee) || 0}
          now={setupNow}
          footnote={setupFootnote}
        />
        {console.log("setup fee here", setUpFee, setupNow, setupFootnote)}
        <PriceCell
          label="Monthly fee"
          was={Number(monthlyFee) || 0}
          now={monthlyNow}
          footnote={monthlyFootnote}
        />
        <PriceCell label="Plan" text={planType || "Not selected"} />
      </div>

      {(rows.length > 0 || hasExplanation) && (
        <div
          className={`border-t border-gray-300 grid gap-px bg-gray-200 ${
            rows.length > 0 && hasExplanation ? "md:grid-cols-[1fr_2fr]" : ""
          }`}
        >
          {rows.length > 0 && (
            <div className="bg-white px-4 py-3">
              <div className="text-sm font-bold text-gray-800 mb-1.5">
                {appliedCount === rows.length
                  ? "Discounts applied"
                  : "Discounts"}
              </div>
              <ul className="list-none p-0 m-0">
                {rows.map((row) => (
                  <li
                    key={row.key}
                    className="flex justify-between items-baseline gap-4 py-1.5"
                  >
                    <span
                      className={`text-sm font-semibold ${
                        row.applied ? "text-gray-800" : "text-gray-500"
                      }`}
                    >
                      {row.label}
                      {!row.applied && (
                        <span className="block text-[12px] font-semibold text-amber-700">
                          {row.unlock}
                        </span>
                      )}
                    </span>
                    <span
                      className={`text-[15px] font-bold whitespace-nowrap ${
                        row.applied
                          ? "text-green-700"
                          : "text-gray-400 line-through"
                      }`}
                    >
                      {row.amount}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasExplanation && (
            <div className="bg-white px-4 py-3">
              <div className="text-sm font-bold text-gray-800 mb-1.5">
                Summary
              </div>
              {paragraphs.map((text, i) => (
                <p
                  key={i}
                  className={`text-sm font-medium text-gray-600 leading-snug m-0 ${
                    i > 0 ? "mt-1.5" : ""
                  }`}
                >
                  {text}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {(showSchedule || showSms) && (
        <div className="border-t border-gray-300 px-4 py-2.5 flex flex-wrap gap-x-8 gap-y-2">
          {showSchedule && (
            <div className="text-[13.5px] font-medium text-gray-600">
              <span className="font-bold text-gray-800">Payment · </span>
              {showInstallmentInfo ? (
                <>
                  {money(dueToday)} today
                  {remaining > 0 && (
                    <>
                      , then {remaining} × {money(dueToday)}
                    </>
                  )}
                </>
              ) : (
                <>
                  available once setup exceeds {money(minimumForInstallments)}
                </>
              )}
            </div>
          )}
          {showSms && (
            <div className="text-[13.5px] font-medium text-gray-600">
              <span className="font-bold text-gray-800">Texts · </span>
              {Number(monthlySMSQuota).toLocaleString("en-US")}/mo
              {priceAdd1KSMS ? `, then ${money(priceAdd1KSMS)} per 1,000` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

OrderSummaryCard.propTypes = {
  setUpFee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  monthlyFee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  planType: PropTypes.string,
  equipDiscount: PropTypes.number,
  equipDiscountType: PropTypes.string,
  qualifyingEquipmentPrice: PropTypes.number,
  discountOnSetupFee: PropTypes.bool,
  planPermDiscount: PropTypes.number,
  planTempDiscount: PropTypes.number,
  tempDiscountExpiryDate: PropTypes.string,
  qualifyingPlanPrice: PropTypes.number,
  discountOnMonthlyFee: PropTypes.bool,
  installments: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dueToday: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minimumForInstallments: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  showInstallmentInfo: PropTypes.bool,
  monthlySMSQuota: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  priceAdd1KSMS: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default OrderSummaryCard;
